import React from 'react';
import loadable from '@loadable/component';

import withPageComponents from '../../../page/components/with-page-components';
import withConfigurations from '../../../product/components/product-configurations';
import useFooterLink from '../pdp-overview/useFooterLink';

import { Helpers } from '../../../core/src/helpers';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import useDeviceInfo from '../../../hooks/useDeviceInfo';

import './pdp-specs.less';

const TechSpecs = loadable(() => import('@hpi-sox/hpstellar-core/TechSpecs'));
const BundlesTechSpecs = loadable(() => import('@hpi-sox/hpstellar-core/BundlesTechSpecs'));
const TechSpecsAccordion = loadable(() => import('./tech-specs-accordion'));

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

//checks if a selected config has tech specs and use those instead
const getSpecs = (pdpTechSpecs, ctoConfig) => {
    const { fullSpecs } = ctoConfig || {};
    const { technical_specifications: ctoTechSpecs = EMPTY_ARRAY } = fullSpecs || EMPTY_OBJECT;
    const { technical_specifications = EMPTY_ARRAY } = pdpTechSpecs || EMPTY_OBJECT;
    //only show specs from selected CTO config if they have been
    if (!ctoTechSpecs || ctoTechSpecs.length < 1) {
        return technical_specifications;
    }
    return ctoTechSpecs;
};

const pdpSpecs = ({ pdpTechSpecs, productInitial, ctoConfigurations }) => {
    const { sku } = productInitial || EMPTY_OBJECT;
    const {
        highlights = EMPTY_ARRAY,
        bundleSpecs = EMPTY_ARRAY,
        translations,
        gtmActions,
        datasheets = EMPTY_ARRAY,
    } = pdpTechSpecs || EMPTY_OBJECT;
    const ctoConfig = ctoConfigurations[sku] || EMPTY_OBJECT;
    const selectedTechSpecs = getSpecs(pdpTechSpecs, ctoConfig);
    const { isBot } = useDeviceInfo(['isBot']);
    const { techSpecs } = useTemplateTheme(['techSpecs']);
    const { title, variant, type } = techSpecs || {};
    const containerId = 'techSpecs';

    const { interceptClick } = useFooterLink();

    let component = null;
    let hasTechSpecs = highlights.length > 0 || selectedTechSpecs.length > 0 || datasheets.length > 0;

    if (bundleSpecs.length > 0) {
        component = (
            <BundlesTechSpecs
                productsData={bundleSpecs}
                translations={translations}
                accordionProps={{ size: 'small' }}
            />
        );
    }

    if (hasTechSpecs) {
        const data = {
            ...pdpTechSpecs,
            highlights: pdpTechSpecs.highlights.map(highlight => ({
                ...highlight,
                value: highlight.value && (
                    <span className="cust-html" dangerouslySetInnerHTML={Helpers.createMarkup(highlight.value)} />
                ),
            })),
            technical_specifications: selectedTechSpecs.map(spec => ({
                ...spec,
                tooltip:
                    spec.tooltip && spec.tooltip.description
                        ? {
                              ...spec.tooltip,
                              description: (
                                  <span
                                      className="cust-html"
                                      dangerouslySetInnerHTML={Helpers.createMarkup(spec.tooltip.description)}
                                  />
                              ),
                          }
                        : spec.tooltip,
                value: spec.value.map(v => ({
                    value: <span className="cust-html" dangerouslySetInnerHTML={Helpers.createMarkup(v.value)} />,
                    subheading: (
                        <span className="cust-html" dangerouslySetInnerHTML={Helpers.createMarkup(v.subheading)} />
                    ),
                })),
            })),
        };

        if (type === 'accordion') {
            component = (
                <TechSpecsAccordion
                    title={title}
                    data={data}
                    translations={translations}
                    variant={variant}
                    containerId={containerId}
                />
            );
        } else {
            component = (
                <TechSpecs
                    className={`tech-specs-content${highlights.length === 0 ? ' slim' : ''}${isBot ? ' seo-expanded' : ''}`}
                    data={data}
                    translations={translations}
                    accordionProps={{ size: 'small', expanded: isBot }}
                    gtmActions={gtmActions}
                    titleTypographyProps={{ className: 'tech-specs-title', variant: 'orionSubtitleLarge' }}
                    wrapperClassName="tech-specs-wrapper"
                />
            );
        }
    }

    return (
        <div
            id={!hasTechSpecs || type === 'accordion' ? containerId : null}
            className="pdpTechSpecs"
            onClick={interceptClick}
        >
            {component}
        </div>
    );
};

export default withConfigurations(withPageComponents(pdpSpecs, { components: ['pdpTechSpecs', 'productInitial'] }));
